import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Mail, CreditCard, User, Database, Lock, AlertCircle } from 'lucide-react';

export const LegalPage = ({ type }) => {
  const navigate = useNavigate();

  const termsContent = (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center pb-6 border-b border-slate-700">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center mx-auto mb-4">
          <FileText className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Regulamin</h1>
        <p className="text-slate-400 text-sm">Ostatnia aktualizacja: Luty 2025</p>
      </div>

      {/* Section 1 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-sky-500/20 flex items-center justify-center text-sm text-sky-400">1</span>
          Postanowienia ogólne
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed pl-9">
          <p>Niniejszy regulamin określa zasady korzystania z aplikacji <strong className="text-white">PromptOrigin</strong>.</p>
          <p>Właścicielem serwisu jest PromptOrigin z siedzibą w Polsce.</p>
          <p>Korzystanie z aplikacji oznacza akceptację niniejszego regulaminu.</p>
        </div>
      </section>

      {/* Section 2 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-sky-500/20 flex items-center justify-center text-sm text-sky-400">2</span>
          Usługi i model subskrypcji
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed pl-9">
          <p>PromptOrigin to rozwijająca się platforma edukacyjna AI zapewniająca stały dostęp do aktualizowanych materiałów, nowych technologii, praktycznych scenariuszy oraz bieżących możliwości wykorzystania sztucznej inteligencji w pracy i biznesie.</p>
          <p>PromptOrigin oferuje:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Stały dostęp do aktualizowanych materiałów edukacyjnych</li>
            <li>Interaktywne ćwiczenia i praktyczne scenariusze</li>
            <li>Bibliotekę gotowych promptów</li>
            <li>Śledzenie postępu nauki</li>
          </ul>
          <p>Pierwsze 3 dni są dostępne bezpłatnie.</p>
          <div className="mt-4 p-4 rounded-xl bg-white/5 border border-slate-700">
            <p className="text-white font-medium mb-2">Model subskrypcji Premium:</p>
            <p className="text-slate-300">
              Zakup subskrypcji Premium zapewnia natychmiastowy dostęp do pierwszych 30 dni materiałów szkoleniowych. 
              Po wykorzystaniu pierwszych 30 dni, użytkownik otrzymuje codziennie dostęp do kolejnego dnia szkoleniowego. 
              Dostęp do nowych materiałów jest ciągły i trwa przez cały okres aktywnej subskrypcji. 
              Platforma jest stale aktualizowana, aby użytkownik mógł pozostawać na bieżąco z dynamicznym rozwojem technologii AI.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-sky-500/20 flex items-center justify-center text-sm text-sky-400">3</span>
          Rejestracja i konto
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed pl-9">
          <p>Aby korzystać z pełnej funkcjonalności, wymagana jest rejestracja.</p>
          <p>Użytkownik zobowiązuje się do:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Podania prawdziwego adresu email</li>
            <li>Zabezpieczenia danych logowania</li>
            <li>Nieudostępniania konta osobom trzecim</li>
          </ul>
        </div>
      </section>

      {/* Section 4 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-sm text-emerald-400">4</span>
          Płatności i subskrypcje
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed pl-9">
          <p>Płatności realizowane są przez bezpieczny system <strong className="text-white">Stripe</strong>.</p>
          <p><strong className="text-white">Dostępne plany:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Plan miesięczny: 35 zł/miesiąc</li>
            <li>Plan roczny: 250 zł/rok (20,83 zł/miesiąc)</li>
          </ul>
          <p>Subskrypcja odnawia się automatycznie. Można ją anulować w dowolnym momencie.</p>
          <p>Po anulowaniu dostęp pozostaje aktywny do końca opłaconego okresu.</p>
        </div>
      </section>

      {/* Section 5 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-sky-500/20 flex items-center justify-center text-sm text-sky-400">5</span>
          Własność intelektualna
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed pl-9">
          <p>Wszystkie treści w aplikacji (teksty, grafiki, prompty) są chronione prawem autorskim.</p>
          <p>Użytkownik może korzystać z promptów do własnych celów, ale nie może ich odsprzedawać ani rozpowszechniać.</p>
        </div>
      </section>
    </div>
  );

  const privacyContent = (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center pb-6 border-b border-slate-700">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-sky-500 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Polityka prywatności</h1>
        <p className="text-slate-400 text-sm">Ostatnia aktualizacja: Luty 2025</p>
      </div>

      {/* Section 1 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-sky-400" />
          Administrator danych
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed">
          <p>Administratorem Twoich danych osobowych jest <strong className="text-white">PromptOrigin</strong>.</p>
        </div>
      </section>

      {/* Section 2 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Database className="w-5 h-5 text-emerald-400" />
          Jakie dane zbieramy
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed">
          <p>Zbieramy następujące dane:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-400">
            <li><strong className="text-white">Adres email</strong> – do logowania i komunikacji</li>
            <li><strong className="text-white">Postęp w nauce</strong> – ukończone lekcje, zdobyte punkty</li>
            <li><strong className="text-white">Dane płatności</strong> – przetwarzane przez Stripe (nie przechowujemy danych karty)</li>
            <li><strong className="text-white">Dane techniczne</strong> – adres IP, typ przeglądarki, dla celów bezpieczeństwa</li>
          </ul>
        </div>
      </section>

      {/* Section 3 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-400" />
          Cel przetwarzania
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed">
          <p>Twoje dane przetwarzamy w celu:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Świadczenia usług (dostęp do platformy)</li>
            <li>Obsługi płatności</li>
            <li>Komunikacji z Tobą</li>
            <li>Poprawy jakości usług</li>
            <li>Zapewnienia bezpieczeństwa</li>
          </ul>
        </div>
      </section>

      {/* Section 4 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Lock className="w-5 h-5 text-sky-400" />
          Bezpieczeństwo danych
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed">
          <p>Stosujemy następujące zabezpieczenia:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Szyfrowanie SSL/TLS</li>
            <li>Hashowanie haseł (bcrypt)</li>
            <li>Regularne kopie zapasowe</li>
            <li>Ograniczony dostęp do danych</li>
          </ul>
        </div>
      </section>

      {/* Section 5 */}
      <section>
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-400" />
          Twoje prawa
        </h2>
        <div className="text-slate-300 space-y-3 text-sm leading-relaxed">
          <p>Masz prawo do:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Dostępu do swoich danych</li>
            <li>Poprawienia danych</li>
            <li>Usunięcia danych (prawo do bycia zapomnianym)</li>
            <li>Przeniesienia danych</li>
            <li>Sprzeciwu wobec przetwarzania</li>
          </ul>
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen pb-24" style={{ background: 'transparent' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 px-6 py-4" style={{ background: 'rgba(5,13,24,0.9)', backdropFilter: 'blur(12px)' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Wróć
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {type === 'privacy' ? privacyContent : termsContent}
      </div>
    </div>
  );
};
